import axios from 'axios';
import { defineStore, storeToRefs } from 'pinia';
import { computed, Ref, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { Loadable, LocationGroup } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import api from '@/api';

import { useCurrentUserStore } from './CurrentUser';
import { useParentEntityStore } from './ParentEntity';

export interface PerformLoadOptions {
  bypassCache?: boolean;
  /**
   * Whether to also load the broader visible set (realm-leader + lineage)
   * used for chip resolution. Set to `true` on any consumer that displays
   * already-persisted group chips, whether or not it also offers a picker.
   */
  includeVisible?: boolean;
}

export interface LocationGroupStore {
  locationGroups: Ref<Loadable<LocationGroup[]>>;
  visibleLocationGroups: Ref<Loadable<LocationGroup[]>>;
  allKnownLocationGroups: Ref<LocationGroup[]>;
  loadLocationGroups: (opts?: PerformLoadOptions) => Promise<void>;
  createLocationGroup: (name: string) => Promise<LocationGroup | undefined>;
  resolveLocationGroups: (naturalIds: string[]) => LocationGroup[];
  formatLocationGroupNames: (naturalIds: string[]) => string;
}

const PERFORM_LOAD_DEFAULT_OPTIONS: Required<PerformLoadOptions> = {
  bypassCache: false,
  includeVisible: false,
};

const _useLocationGroupStore = defineStore(
  'locationGroup',
  (): LocationGroupStore => {
    const { selectedDomainId, isAuthenticatedUser } = useCurrentUserStore();
    const { owningClient, includeSubDomains } = useParentEntityStore();
    const { t } = useI18n();
    const toast = useToast();

    const locationGroups: Ref<Loadable<LocationGroup[]>> = ref({
      loading: false,
    });

    /** Broader display-only set (realm-leader + lineage) for chip resolution. */
    const visibleLocationGroups: Ref<Loadable<LocationGroup[]>> = ref({
      loading: false,
    });

    /**
     * Tracks whether the visible set has been fetched at least once, so the
     * tenant-switch watcher only refetches it for consumers that need it.
     */
    const visibleHasBeenLoaded = ref(false);

    /**
     * The tenant the user is currently operating as. Snapshotted at request
     * dispatch and compared at response time to discard responses that
     * arrive after the user has switched tenants.
     */
    const tenantScope = computed(
      () => `${selectedDomainId.value ?? ''}:${owningClient.value?.id ?? ''}`,
    );

    /**
     * Wider snapshot for the assignable endpoint, which also depends on
     * subdomain scope. Visible endpoint uses `tenantScope` alone.
     */
    const assignableScope = computed(
      () => `${tenantScope.value}:${includeSubDomains.value}`,
    );

    /**
     * Assignable set unioned with the visible set. Assignable wins on
     * overlap so chip identity matches the autocomplete option.
     */
    const groupLookup = computed(() => {
      const byNaturalId = new Map<string, LocationGroup>();

      for (const group of visibleLocationGroups.value.data ?? []) {
        if (group.naturalId) {
          byNaturalId.set(group.naturalId, group);
        }
      }

      for (const group of locationGroups.value.data ?? []) {
        if (group.naturalId) {
          byNaturalId.set(group.naturalId, group);
        }
      }

      return byNaturalId;
    });

    const allKnownLocationGroups = computed(() =>
      Array.from(groupLookup.value.values()).sort((a, b) =>
        (a.name ?? '').localeCompare(b.name ?? ''),
      ),
    );

    const loadLocationGroups = async (opts: PerformLoadOptions = {}) => {
      const options: Required<PerformLoadOptions> = {
        ...PERFORM_LOAD_DEFAULT_OPTIONS,
        ...opts,
      };

      // Empty array counts as loaded so no-groups domains don't refetch on
      // every mount.
      const hasData = locationGroups.value.data !== undefined;
      const hasVisibleData = visibleLocationGroups.value.data !== undefined;

      const shouldLoadAssignable =
        options.bypassCache || (!hasData && !locationGroups.value.loading);

      const shouldLoadVisible =
        options.includeVisible &&
        (options.bypassCache ||
          (!hasVisibleData && !visibleLocationGroups.value.loading));

      const pending: Promise<void>[] = [];

      if (shouldLoadAssignable) {
        pending.push(performLoad());
      }

      if (shouldLoadVisible) {
        pending.push(performVisibleLoad());
      }

      await Promise.all(pending);
    };

    const performLoad = async () => {
      if (!isAuthenticatedUser.value) {
        return;
      }

      const dispatchedIn = assignableScope.value;
      const isStale = () => dispatchedIn !== assignableScope.value;

      try {
        locationGroups.value.loading = true;
        const data = await api.locationGroup.findAll(includeSubDomains.value);

        if (isStale()) {
          return;
        }

        locationGroups.value.data = data;
      } catch (e) {
        if (axios.isCancel(e) || isStale()) {
          return;
        }

        handleLoadError(locationGroups, e);
      } finally {
        if (!isStale()) {
          locationGroups.value.loading = false;
        }
      }
    };

    const performVisibleLoad = async () => {
      if (!isAuthenticatedUser.value) {
        return;
      }

      const dispatchedIn = tenantScope.value;
      const isStale = () => dispatchedIn !== tenantScope.value;

      visibleHasBeenLoaded.value = true;

      try {
        visibleLocationGroups.value.loading = true;
        const data = await api.locationGroup.findAllVisible();

        if (isStale()) {
          return;
        }

        visibleLocationGroups.value.data = data;
      } catch (e) {
        if (axios.isCancel(e) || isStale()) {
          return;
        }

        handleLoadError(visibleLocationGroups, e);
      } finally {
        if (!isStale()) {
          visibleLocationGroups.value.loading = false;
        }
      }
    };

    const createLocationGroup = async (
      name: string,
    ): Promise<LocationGroup | undefined> => {
      // Drop the response if the user switched tenants mid-POST, so a
      // Tenant A naturalId can't get stamped onto a Tenant B entity.
      const dispatchedIn = tenantScope.value;

      try {
        const created = await api.locationGroup.save({ name });

        if (dispatchedIn !== tenantScope.value) {
          return undefined;
        }

        const existing = locationGroups.value.data ?? [];

        locationGroups.value.data = [...existing, created].sort((a, b) =>
          (a.name ?? '').localeCompare(b.name ?? ''),
        );

        return created;
      } catch (e) {
        if (dispatchedIn !== tenantScope.value) {
          return undefined;
        }

        handleCreateError(e);
        return undefined;
      }
    };

    const resolveLocationGroups = (naturalIds: string[]): LocationGroup[] => {
      if (!naturalIds.length) {
        return [];
      }

      return naturalIds
        .map((naturalId) => groupLookup.value.get(naturalId))
        .filter(notNull);
    };

    const formatLocationGroupNames = (naturalIds: string[]): string => {
      if (!naturalIds.length) {
        return '';
      }

      return Array.from(new Set(naturalIds))
        .map((naturalId) => groupLookup.value.get(naturalId)?.name || naturalId)
        .filter((name) => name.length > 0)
        .sort((a, b) => a.localeCompare(b))
        .join(', ');
    };

    const handleLoadError = <T>(loadable: Ref<Loadable<T[]>>, e: unknown) => {
      loadable.value.error = e;
      console.error('Error loading location groups', e);
      toast.addError({
        detail: t('errors.loadingParam', {
          param: t('locationGroups.locationGroups', 2),
        }),
        error: e,
      });
    };

    const handleCreateError = (e: unknown) => {
      console.error('Error creating location group', e);
      toast.addError({
        detail: t('errors.savingParam', {
          param: t('locationGroups.locationGroups'),
        }),
        error: e,
      });
    };

    // Reset and refetch on tenant/scope switch. The active reload covers
    // consumers that stay mounted across the switch and only load on mount.
    watch(
      [selectedDomainId, () => owningClient.value?.id, includeSubDomains],
      () => {
        locationGroups.value.data = [];
        performLoad();

        if (visibleHasBeenLoaded.value) {
          visibleLocationGroups.value.data = [];
          performVisibleLoad();
        }
      },
    );

    return {
      locationGroups,
      visibleLocationGroups,
      allKnownLocationGroups,
      loadLocationGroups,
      createLocationGroup,
      resolveLocationGroups,
      formatLocationGroupNames,
    };
  },
);

export const useLocationGroupStore = (): LocationGroupStore => {
  const store = _useLocationGroupStore();
  const { locationGroups, visibleLocationGroups, allKnownLocationGroups } =
    storeToRefs(store);
  const {
    loadLocationGroups,
    createLocationGroup,
    resolveLocationGroups,
    formatLocationGroupNames,
  } = store;

  return {
    locationGroups,
    visibleLocationGroups,
    allKnownLocationGroups,
    loadLocationGroups,
    createLocationGroup,
    resolveLocationGroups,
    formatLocationGroupNames,
  };
};
